import { Button, H2, InputNote, InputText } from "@sellar-io/components";
import { signInWithCustomToken } from "firebase/auth";
import { useState } from "react";
import {
  isSignInConnectionError,
  signInConnectionMessage,
} from "../../../../hooks/signInConnection";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { SpinnerLegacy } from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { useAuthCreateSignupCode } from "../../../../hooks/api/v2/auth/useAuthCreateSignupCode";
import { useAuthVerifyToken } from "../../../../hooks/api/v2/auth/useAuthVerifyToken";
import { useFirebaseUser } from "../../../../hooks/useFirebaseUser.shared";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../hooks/useSignupContext.shared";
import { Mixpanel } from "../../../../utils/mixpanel";
import TagManager from "react-gtm-module";

type VeryTokenFormType = {
  accessToken: string | null;
};
export const UserSignupVerificationCodeStep = ({
  email,
}: {
  email: string;
}) => {
  const [searchParams] = useSearchParams();
  const { auth } = useFirebaseUser();
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState<string>();
  const [loginToken, setLoginToken] = useState<string>();
  const [attemptsRemaining, setAttemptsRemaining] = useState<number>(3);
  const [resendSuccessMessage, setResendSuccessMessage] = useState<string>();
  const form = useForm<VeryTokenFormType>({
    shouldUseNativeValidation: true,
    defaultValues: {
      accessToken: null,
    },
  });

  const { setStep } = useSignupContext();
  const authVerifyTokenMutation = useAuthVerifyToken();
  const authSignupCodeMutation = useAuthCreateSignupCode();
  const signIn = async (token: string) => {
    setSigningIn(true);
    setSignInError(undefined);
    try {
      await signInWithCustomToken(auth, token);
      setStep(SignupFlowStep.SignupDetails);
    } catch (error: unknown) {
      setSignInError(
        isSignInConnectionError(error)
          ? signInConnectionMessage
          : "We couldn't sign you in. Please try again.",
      );
    } finally {
      setSigningIn(false);
    }
  };
  const loading = authVerifyTokenMutation.isPending || signingIn;
  const onSubmit = (data: VeryTokenFormType) => {
    if (loading) return;
    if (loginToken) {
      void signIn(loginToken);
      return;
    }
    if (!data.accessToken) throw new Error();

    setSignInError(undefined);

    const storefrontLinkPassword =
      searchParams.get("storefrontLink") ?? searchParams.get("priceTierLink");
    const businessUserInviteJwt = searchParams.get("jwt");
    const utmMedium = searchParams.get("utm_medium");

    setResendSuccessMessage(undefined);
    authVerifyTokenMutation.mutate(
      {
        data: {
          email,
          accessToken: parseInt(data.accessToken),
        },
        sellarMetadata: {
          storefrontLinkPassword,
          businessUserInviteJwt,
          utmMedium,
          mixpanelDistinctId: Mixpanel.getDistinctId(),
        },
      },
      {
        onSuccess: (res) => {
          TagManager.dataLayer({
            dataLayer: {
              event: "verification_code_entered",
            },
          });

          setLoginToken(res.customLoginToken);
          void signIn(res.customLoginToken);
        },
        onError: (error) => {
          if (isSignInConnectionError(error)) return;
          setAttemptsRemaining(attemptsRemaining - 1);
          if (attemptsRemaining === 1) {
            setStep(SignupFlowStep.EmailStep);
          }
        },
      },
    );
  };

  const onResendCode = () => {
    if (loading || authSignupCodeMutation.isPending) return;
    setLoginToken(undefined);
    setSignInError(undefined);
    const storefrontLinkPassword =
      searchParams.get("storefrontLink") ?? searchParams.get("priceTierLink");
    const businessUserInviteJwt = searchParams.get("jwt");
    const utmMedium = searchParams.get("utm_medium");

    setResendSuccessMessage(undefined);
    authSignupCodeMutation.mutate(
      {
        data: {
          email,
        },
        sellarMetadata: {
          storefrontLinkPassword,
          businessUserInviteJwt,
          utmMedium,
          mixpanelDistinctId: Mixpanel.getDistinctId(),
        },
      },
      {
        onSuccess: () => {
          setResendSuccessMessage(`Code re-sent to ${email}`);
          TagManager.dataLayer({
            dataLayer: {
              event: "email_address_reentered",
              email,
            },
          });
        },
      },
    );
  };
  return (
    <>
      <div className={"m-auto flex h-full flex-col justify-center gap-4"}>
        <div className={"flex flex-col gap-2 text-center"}>
          <H2>
            Thanks! Please enter the verification code we've sent to your inbox
          </H2>
        </div>
        {signInError && <Alert type="error" message={signInError} />}
        {authVerifyTokenMutation.isError && (
          <Alert
            type={"error"}
            message={
              isSignInConnectionError(authVerifyTokenMutation.error)
                ? signInConnectionMessage
                : (apiV2AssertError(authVerifyTokenMutation.error)?.response
                    ?.data.message ?? "Unknown error")
            }
            className={"w-full"}
          />
        )}
        {authSignupCodeMutation.isError && (
          <Alert
            type={"error"}
            message={
              isSignInConnectionError(authSignupCodeMutation.error)
                ? signInConnectionMessage
                : (apiV2AssertError(authSignupCodeMutation.error)?.response
                    ?.data.message ?? "Unknown error")
            }
            className={"w-full"}
          />
        )}
        {resendSuccessMessage && (
          <Alert
            message={resendSuccessMessage}
            type={"success"}
            className={"w-full"}
          />
        )}
        <form
          className={"flex flex-col gap-2"}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <InputLabel>Check your inbox and enter the code here</InputLabel>
          <InputContainer>
            <InputText
              {...form.register("accessToken", {
                required: true,
                disabled: loading || Boolean(loginToken),
              })}
            ></InputText>
          </InputContainer>
          {attemptsRemaining < 3 && (
            <InputNote>
              <span className={"text-red-700"}>
                {attemptsRemaining} attempts remaining
              </span>
            </InputNote>
          )}
          <Button
            type={"submit"}
            className={"mt-4 w-full"}
            loading={loading}
            disabled={loading}
          >
            Continue
          </Button>
        </form>
        <div className={"flex flex-col items-center gap-4"}>
          <p>
            Didn't receive a code?{" "}
            <span
              color={"tertiary"}
              onClick={onResendCode}
              className={"cursor-pointer underline"}
            >
              {authSignupCodeMutation.isPending ? (
                <SpinnerLegacy />
              ) : (
                "Re-send another"
              )}
            </span>
          </p>
        </div>
      </div>
    </>
  );
};
